/* eslint-disable @next/next/no-img-element */
import { formatCurrency } from '@/helpers/format';
import usePrefix from '@/hooks/usePrefix';
import { useMobxStores } from '@/stores';
import {
  Affix,
  Button,
  Divider,
  Empty,
  Popover,
  Rate,
  Skeleton,
  Tabs,
} from 'antd';
import Image from 'next/image';
import { observer } from 'mobx-react-lite';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState, useEffect, useRef } from 'react';
import ButtonStatus from './ButtonStatus2';
import VerticalEX from './FacilityHighlights/VerticalEX';
import SidePopover from './FacilityHighlights/SidePopover';
import Vertical from './FacilityHighlights/Vertical';
import {
  DownCircleOutlined,
  EnvironmentFilled,
  UpCircleOutlined,
} from './Icons';
import SearchImage from './SearchImage';

import { isShowHighlights } from '@/helpers/highlights';
import FacilityTypeDeal from '@/utils/facilityTypeDeal';
import { CDN1, CDN2 } from '@/helpers/api';
import { mapRateSearchName, mapReviewStars } from '@/utils/tools';
import LowestPriceGuarantee from '@/components/LowestPriceGuarantee';
import { getParkingAtPath, getUrlCodeOrSlug } from '@/utils/newparkingatroutes';
import NeedMoreData from './NeedMoreData';
import { IAirport } from '@/types';
import { isServer } from '@/utils/isServer';
import { setStorageCheckinCheckout } from '@/hooks/useLocalstorage';
import { useTranslation } from 'react-i18next';

function checkShowFull(facility_url_code: string) {
  return !facility_url_code?.toLocaleLowerCase().startsWith('arv');
}

const Card = ({
  configInfo,
  item,
  index,
  cardsRef,
  checkin,
  checkout,
  hasHighlight,
  deviceType,
  isCity,
}) => {
  const prefix = usePrefix();
  const router = useRouter();
  const { searchStore } = useMobxStores();
  const [showWhatParkBtn, setShowWhatParkBtn] = useState<boolean>(false);
  const [activeKey, setActiveKey] = useState<string>('description');
  const [popoverVisible, setPopoverVisible] = useState<boolean>(false);

  const { TabPane } = Tabs;

  const openMoreInformation = async (e): Promise<void> => {
    e.preventDefault();
    setShowWhatParkBtn(!showWhatParkBtn);
    setActiveKey('description');
    return;
  };

  const openViewHighlights = async (e): Promise<void> => {
    e.preventDefault();
    setShowWhatParkBtn(!showWhatParkBtn);
    setActiveKey('highlights');
    return;
  };

  function preventClick(e) {
    if (!e) e = window.event;
    e.stopPropagation();
    return;
  }

  useEffect(() => {
    sessionStorage.removeItem('checkin');
    sessionStorage.removeItem('checkout');
  }, []);

  const UnbrandedImages = [
    CDN2() + '/staticmyapp/cards-default-image.webp',
    CDN2() + '/staticmyapp/pvd-airport.webp',
    CDN2() + '/staticmyapp/default-image-3.webp',
  ];

  let lastUnbrandedIndex = null;

  const getUnbrandedImage = () => {
    let randomIndex = index % UnbrandedImages.length;
    if (randomIndex === lastUnbrandedIndex) {
      randomIndex = (randomIndex + 1) % UnbrandedImages.length;
    }
    lastUnbrandedIndex = randomIndex;
    return UnbrandedImages[randomIndex];
  };

  let imageUrl = item.facility_is_image_uploaded
    ? `${CDN1}/upload/${configInfo.environment}/${item.facility_id}`
    : getUnbrandedImage();

  const arriveLogo = item.highlights.find((x) => x.type === 'arrive_logo_url');
  if (arriveLogo) {
    imageUrl = arriveLogo.description;
  }

  return item.date_sold_out > 0 ? (
    <>
      <div
        key={item.facility_id + index}
        ref={(el) => (cardsRef.current[index] = el)}
        className={`relative mx-auto bg-white rounded-[0.675rem] border shadow-lg overflow-hidden  transition-all duration-300 ${item.selected ? 'shadow-2xl' : ''} opacity-60`}
      >
        <div
          className="flex flex-col xl:flex-row w-full "
        >
          <div className="xl:flex-[0_0_20%] flex justify-center items-center p-2">
            <div className="relative w-full h-24">
              <SearchImage
                src={imageUrl}
                native={!!arriveLogo}
                placeholder="blur"
                fill
                style={{ objectFit: 'contain' }}
                blurDataURL={getUnbrandedImage()}
                errorImage={getUnbrandedImage()}
                alt={`Airport: ${item.facility_lot} Background`}
                key={item.facility_id}
                quality={100}
              />
            </div>
          </div>
          <div className="w-full flex flex-col space-y-4 ">
            <div className="flex flex-row w-full p-4">
              <div className="flex flex-col w-full col-span-2 px-0 self-center mx-auto">
                <div className="w-full xl:w-[23rem] space-y-2">
                  <h3 className="text-lg font-semibold text-left w-3/5 truncate">
                    {item.facility_lot}
                  </h3>
                  {checkShowFull(item.facility_url_code) && (
                    <div className="flex flex-row items-center gap-2">
                      <Rate
                        allowHalf
                        disabled
                        value={1}
                        count={1}
                        className='text-base'
                      />
                      <p className=" text-gray-500">
                        ({item.num_review} {item.num_review === 1 ? 'review' : 'reviews'})
                      </p>
                    </div>
                  )}
                </div>
              </div>
              {item.facilityParkingType == 3 ||
                item.facilityParkingType == 4 ? (
                <p className="w-full text-base text-right">
                  {formatCurrency({
                    value: item.facility_solid_selling_price
                      ? item.facility_solid_selling_price
                      : item.facility_afee,
                    locale: 'en-US',
                    currency: 'USD',
                  })} {' '}
                  <small className="text-sm text-onair-gray">
                    {item.facilityParkingType == 3
                      ? 'Sleep and Park'
                      : 'Monthly Park'}
                  </small>
                </p>
              ) : (
                <p className="w-full text-base text-right">
                  {formatCurrency({
                    value: item.facility_selling_price,
                    locale: 'en-US',
                    currency: 'USD',
                  })}{' '}
                  <small className="text-sm text-onair-gray lowercase">
                    {mapRateSearchName(item)}
                  </small>
                </p>
              )}
            </div>
          </div>
        </div>
        <div
          className={`w-full overflow-hidden flex justify-between border-t mt-2 py-2 px-4`}
        >
          <ButtonStatus
            status={searchStore.facilityStatus(item)}
            facility_url_code={item.facility_url_code}
            minDays={item.facility_min_days}
            maxDays={item.facility_max_days}
            configInfo={configInfo}
            item={item}
          />
        </div>
      </div>
    </>
  ) : (
    <div
      key={item.facility_id + index}
      ref={(el) => (cardsRef.current[index] = el)}
      className="border rounded-md overflow-hidden shadow-md transition-all duration-300 bg-white"
    >
      <div
        className="flex flex-col xl:flex-row w-full relative"
      >
        {item.facility_id == searchStore.cheapestFacility && (
          <>
            <div
              className={`z-10 absolute left-0 top-0 cursor-default flex flex-shrink-0 items-center
            w-0 h-0 bg-transparent  border-solid border-r-[85px] border-t-[85px] border-r-transparent border-t-primary rounded-t-md rounded-r-none
            `}
            ></div>
            <p className="p-0.5 -rotate-45 absolute left-2 top-1 z-10 text-white font-bold">
              Best <br /> Price!
            </p>
          </>
        )}
        <div className="xl:flex-[0_0_20%] flex justify-center items-center p-2">
          <div className="relative w-32 h-32 content-center">
            <SearchImage
              src={imageUrl}
              native={!!arriveLogo}
              placeholder="blur"
              fill
              style={{ objectFit: 'contain' }}
              blurDataURL={getUnbrandedImage()}
              errorImage={getUnbrandedImage()}
              alt={`Airport: ${item.facility_lot} Background`}
              key={item.facility_id}
              quality={100}
            />
          </div>
        </div>
        <div className="flex flex-row w-full">
          <div className="xl:flex-[1_2_auto] px-2 py-4 flex flex-col justify-center w-auto space-y-2">
            {item.facility_is_express_deal > 0 && (
              <p className=" font-semibold text-xs italic text-primary">{FacilityTypeDeal(item)}</p>
            )}

            <h3 className="text-base xl:text-lg font-semibold truncate max-w-[13rem] xl:max-w-[18rem]">
              {item.facility_lot}
            </h3>
            {!isCity && (
              <p className="text-xs">
                <EnvironmentFilled /> {item.facility_airport_distance}{' '}
                {item.facility_airport_distance === 1
                  ? 'mile'
                  : 'miles'}
              </p>
            )}
            {checkShowFull(item.facility_url_code) && (
              <>
                <div className="flex items-center gap-2 mt-1">
                  <p
                    className={`text-white font-semibold rounded-md px-2 py-1 text-sm flex items-center justify-center bg-gray-800`}
                  >
                    {item.facility_review_avg.toFixed(1)}
                    <span className="ml-0.5 text-sm font-normal">/5</span>
                  </p>
                  <Rate
                    allowHalf
                    disabled
                    value={1}
                    count={1}
                    className='text-base'
                  />
                  <p className=" text-gray-500">
                    ({item.num_review} {item.num_review === 1 ? 'review' : 'reviews'})
                  </p>
                </div>

              </>
            )}
            {isShowHighlights(item.highlights) ? (
              <Popover
                content={
                  <SidePopover
                    facility={item}
                    highlights={item.highlights}
                    size="small"
                  />
                }
                trigger={deviceType === 'desktop' ? 'hover' : 'click'}
                overlayClassName={`${deviceType !== 'desktop' ? 'search-list-popover' : 'search-list-popover-desktop'}`}
              >
                <VerticalEX
                  facility={item}
                  highlights={item.highlights}
                  size="small"
                  className="grid grid-cols-1 xl:grid-cols-2 gap-2 xl:gap-4 items-start justify-items-start"
                  isCity={isCity}
                />
              </Popover>
            ) : (
              <></>
            )}
            {!item.facility_disable_lowest_price ? (
              <div className="flex flex-row items-center gap-2">
                <p
                  className={`font-semibold rounded-md px-2 py-1 text-sm flex items-center justify-center bg-[#069B27]/10`}
                >
                  Lowest Price Guarantee*
                </p>
              </div>
            ) : < div />}
          </div>
          <div className="px-2 py-4 flex flex-col justify-between xl:items-end text-right w-full xl:max-w-[12rem] xl:self-center">
            {item.facilityParkingType == 3 ||
              item.facilityParkingType == 4 ? (
              <div className="flex flex-col justify-center items-end text-right gap-4 w-full">
                <div>
                  <p className="text-base">
                    {formatCurrency({
                      value: item.facility_solid_selling_price
                        ? item.facility_solid_selling_price
                        : item.facility_afee,
                      locale: 'en-US',
                      currency: 'USD',
                    })} {' '}
                    <small className="text-sm text-onair-gray">
                      {item.facilityParkingType == 3
                        ? 'Sleep and Park'
                        : 'Monthly Park'}
                    </small>
                  </p>
                  <p className="text-xl font-bold text-primary lowercase">
                    {formatCurrency({
                      value: item.facility_total_price,
                      locale: 'en-US',
                      currency: 'USD',
                    })}{' '}
                    <small className="text-base text-onair-gray font-bold">
                      total
                    </small>
                  </p>
                  <small className="text-[0.7rem] xl:text-xs text-green-600">
                    Includes taxes and fees
                  </small>
                </div>
                <Button
                  type="primary"
                  className="w-auto rounded-full"
                  size="large"
                  href={`${prefix}/${getParkingAtPath(
                    item
                  )}/${getUrlCodeOrSlug(item)}`}
                >
                  <span className="font-bold">
                    {item.facilityParkingType == 3
                      ? 'Sleep & Park'
                      : item.facilityParkingType == 4
                        ? 'Monthly Park'
                        : 'Park Here'}
                  </span>
                </Button>
              </div>
            ) : (
              <div className="flex flex-col justify-center items-end text-right gap-4 w-full">
                <div>
                  <p className="text-base">
                    {formatCurrency({
                      value: item.facility_selling_price,
                      locale: 'en-US',
                      currency: 'USD',
                    })}{' '}
                    <small className="text-sm text-onair-gray lowercase">
                      {mapRateSearchName(item)}
                    </small>
                  </p>
                  <p className="text-xl font-bold text-primary">
                    {formatCurrency({
                      value: item.facility_total_price,
                      locale: 'en-US',
                      currency: 'USD',
                    })}{' '}
                    <small className="text-base text-onair-gray font-bold">
                      total
                    </small>
                  </p>
                  <small className="text-[0.7rem] xl:text-xs text-green-600">
                    Includes taxes and fees
                  </small>
                </div>
                <Button
                  type="primary"
                  className="w-auto rounded-full"
                  size="large"
                  href={`${prefix}/${getParkingAtPath(
                    item
                  )}/${getUrlCodeOrSlug(item)}`}
                >
                  <span className="font-bold">
                    {item.facilityParkingType == 3
                      ? 'Sleep & Park'
                      : item.facilityParkingType == 4
                        ? 'Monthly Park'
                        : 'Park Here'}
                  </span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        className="h-10 w-full border-t bg-transparent text-xs flex flex-row items-center justify-between px-2" >
        <p className="text-gray-300 opacity-50 !text-xs cursor-default ">
          {item.facility_url_code}
        </p>
        <div className="flex flex-row">
          <button
            onClick={openMoreInformation}
            className="h-10 w-full border-none bg-transparent text-xs flex flex-row items-center">
            <p className="mr-2">What is the parking address?</p>
            {showWhatParkBtn ? (
              <UpCircleOutlined />
            ) : (
              <DownCircleOutlined />
            )}
          </button>
        </div>
      </div>
      <div
        className={`w-full overflow-hidden transition-all duration-300 ${showWhatParkBtn ? 'max-h-full opacity-100' : 'max-h-0 opacity-0'
          }`}
      >
        <div className="border-t px-4 py-3"
          onClick={preventClick}
        >
          <Tabs
            activeKey={activeKey}
            onChange={(k) => setActiveKey(k)}
            type="card"
            size="large"
            className="tab-reservation-2"
          >
            <TabPane
              key="description"
              tab={
                <span className="text-base flex flex-col items-center align-center">
                  <p>Address</p>
                </span>
              }
            >
              <div className="flex flex-col w-full">
                <div className="px-4">
                  {item.facility_branded == 1 ? (
                    <>
                      <p className="text-left my-2">
                        {item.facility_address}
                      </p>
                      <p className="text-left my-2">
                        {item.facility_short_description}
                      </p>
                    </>
                  ) : (
                    <span>{configInfo.unbrandedAddress}</span>
                  )}
                  {item.facility_branded != 1 &&
                    item.location_guaranteed &&
                    item.location_guaranteed.length > 0 && (
                      <div>
                        <p className="font-bold text-left my-2">
                          Guaranteed parking at one of these great lots
                        </p>
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                          {item.location_guaranteed.map(
                            (guaranteedLocation) => (
                              <>
                                <div className="border border-gray p-2 rounded-md">
                                  <Rate
                                    allowHalf
                                    disabled
                                    value={guaranteedLocation.stars}
                                    className="text-left !text-xs mb-2"
                                  />
                                  <p className="text-white font-semibold rounded-md w-12 text-sm text-center bg-deal/90">
                                    {guaranteedLocation.range.toFixed(1)}+
                                  </p>
                                  <p className="font-bold text-left mt-2 line-clamp-1">
                                    {guaranteedLocation.name}
                                  </p>
                                </div>
                              </>
                            )
                          )}
                        </div>
                      </div>
                    )}
                </div>
              </div>
            </TabPane>
            {isShowHighlights(item.highlights) ? (
              <TabPane
                key="highlights"
                tab={
                  <span className="text-base flex flex-col items-center align-center">
                    <p>Highlights</p>
                  </span>
                }
              >
                <div className="flex flex-col !w-full">
                  <Vertical
                    facility={item}
                    highlights={item.highlights}
                    size="small"
                  />

                </div>
              </TabPane>
            ) : (
              <></>
            )}
          </Tabs>
        </div>
      </div>
    </div>
  );
};

function SearchList({
  configInfo,
  checkin,
  checkout,
  deviceType,
  searchAlternate,
  currentAirport,
  isNewLanding,
  isCity,
  testScope,
}: {
  configInfo: any;
  checkin: string;
  checkout: string;
  deviceType: string;
  searchAlternate: any;
  currentAirport?: IAirport;
  isNewLanding?: boolean;
  isCity?: boolean;
  testScope?: any;
}) {
  const { searchStore } = useMobxStores();
  const cardsRef = useRef<any>([]);
  const { t } = useTranslation();

  const hasHighlight = searchStore.filteredFacilities?.some(
    (x) => x.facility_branded == 1
  );

  const baseFacilities =
    searchStore.filteredFacilities ?? searchAlternate?.result;


  const showLowestPriceMessage = baseFacilities?.some(
    (item) => item.facility_disable_lowest_price === 0
  );
  useEffect(() => {
    if (!isServer) {
      setStorageCheckinCheckout(checkin, checkout);
    }
  }, [checkin, checkout]);
  return (
    // <Skeleton
    //   active
    //   loading={searchStore.isLoading}
    //   className="max-w-2xl mx-auto"
    // >
    <>
      {baseFacilities?.map((item, index) => {
        return (
          <div key={index} className="mb-4 mt-2 px-2">
            <Card
              configInfo={configInfo}
              item={item}
              index={index}
              cardsRef={cardsRef}
              checkin={checkin}
              checkout={checkout}
              hasHighlight={hasHighlight}
              deviceType={deviceType}
              isCity={isCity}
            />
          </div>
        );
      })}
      {!isNewLanding && showLowestPriceMessage && (
        <p className="text-[0.625rem] ml-2 mt-1.5 mb-4 xl:mb-2 xl:text-sm">
          {t('price_match_guarantee')}
        </p>
      )}

      {searchStore.filteredFacilities?.length === 0 && (
        <NeedMoreData airport={currentAirport} />
      )}
    </>
  );
}

export default observer(SearchList);
